import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import { Helmet } from "react-helmet-async";
import { Briefcase, RefreshCw, LogOut, Save, MessageSquare, User, MapPin, DollarSign, Clock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_STORAGE = "bw_admin_key";

const STATUS_META = {
  requested:   { label: "Requested",   color: "var(--bw-warning)" },
  reviewing:   { label: "Reviewing",   color: "var(--bw-brand)" },
  quoted:      { label: "Quoted",      color: "var(--bw-brand)" },
  in_progress: { label: "In Progress", color: "var(--bw-accent)" },
  submitted:   { label: "Submitted",   color: "var(--bw-accent)" },
  completed:   { label: "Completed",   color: "var(--bw-success)" },
  cancelled:   { label: "Cancelled",   color: "var(--bw-text-tertiary)" },
};

function adminApi(key) {
  return axios.create({
    baseURL: API,
    headers: { "X-Admin-Key": key, "Content-Type": "application/json" },
  });
}

export default function AdminConcierge() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const client = useMemo(() => (adminKey ? adminApi(adminKey) : null), [adminKey]);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true); setError("");
    try {
      const { data } = await client.get(`/admin/concierge/queue${filter ? `?status=${filter}` : ""}`);
      setItems(data.items || []);
      setStats(data.stats || {});
      setStatuses(data.statuses || []);
    } catch (err) {
      if (err.response?.status === 403) {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey("");
        setError("Admin key rejected");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [client, filter]);

  useEffect(() => { load(); }, [load]);

  const submitKey = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    localStorage.setItem(ADMIN_KEY_STORAGE, k);
    setAdminKey(k);
    setKeyInput("");
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey("");
    setItems([]);
  };

  const selected = items.find((i) => i.id === selectedId) || null;
  const totalRequests = Object.values(stats).reduce((a, b) => a + (b || 0), 0);

  const updateStatus = async (id, newStatus) => {
    try {
      await client.post(`/admin/concierge/${id}/status`, { status: newStatus });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const addNote = async (id, note) => {
    if (!note.trim()) return;
    try {
      await client.post(`/admin/concierge/${id}/note`, { note });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
        <Helmet>
          <title>Admin — BuildWise AI</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Header />
        <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-key-gate">
          <div className="label-overline mb-3">Admin Access</div>
          <h1 className="font-display font-black text-4xl tracking-tight mb-6" style={{ color: "var(--bw-text-primary)" }}>
            Concierge Queue
          </h1>
          <p className="mb-6 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
            Enter your admin key to access the concierge queue dashboard.
          </p>
          <form onSubmit={submitKey}>
            <label className="label-overline block mb-2">Admin Key</label>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              className="bw-input"
              placeholder="bw-admin-…"
              autoFocus
              data-testid="admin-key-input"
            />
            <button type="submit" className="btn-primary w-full mt-4" data-testid="admin-key-submit">
              Enter Dashboard
            </button>
            {error && (
              <div className="mt-4 text-sm p-3 border" style={{ borderColor: "var(--bw-error)", color: "var(--bw-error)" }}>
                {error}
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Helmet>
        <title>Concierge Queue — BuildWise AI Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-10" data-testid="admin-concierge-page">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-overline mb-2 flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--bw-accent)" }} />
              Admin · Concierge Queue
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight" style={{ color: "var(--bw-text-primary)" }}>
              Permit Requests
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-outline text-sm" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="btn-outline text-sm" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Exit
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-6">
          <button
            onClick={() => setFilter("")}
            className={`bw-card p-3 text-left ${filter === "" ? "border-2" : ""}`}
            style={{ borderColor: filter === "" ? "var(--bw-text-primary)" : "var(--bw-border)" }}
            data-testid="stat-all"
          >
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-text-tertiary)" }}>All</div>
            <div className="font-display font-black text-2xl mt-1" style={{ color: "var(--bw-text-primary)" }}>
              {totalRequests}
            </div>
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`bw-card p-3 text-left ${filter === s ? "border-2" : ""}`}
              style={{ borderColor: filter === s ? STATUS_META[s]?.color : "var(--bw-border)" }}
              data-testid={`stat-${s}`}
            >
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: STATUS_META[s]?.color }}>
                {STATUS_META[s]?.label || s}
              </div>
              <div className="font-display font-black text-2xl mt-1" style={{ color: "var(--bw-text-primary)" }}>
                {stats[s] || 0}
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 text-sm p-3 border" style={{ borderColor: "var(--bw-error)", color: "var(--bw-error)" }}>
            {error}
          </div>
        )}

        {/* Main content: list + detail split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* List */}
          <div className="lg:col-span-7 space-y-2">
            {loading && items.length === 0 && <div className="label-overline">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="bw-card p-16 text-center">
                <Briefcase className="w-10 h-10 mx-auto mb-4" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
                <h3 className="font-display font-bold text-2xl mb-2" style={{ color: "var(--bw-text-primary)" }}>No requests</h3>
                <p className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>
                  {filter ? `No requests with status "${filter}"` : "The queue is clear."}
                </p>
              </div>
            )}
            {items.map((item) => (
              <QueueCard
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                onClick={() => setSelectedId(item.id)}
              />
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-5">
            {selected ? (
              <QueueDetail
                item={selected}
                statuses={statuses}
                onStatusChange={(s) => updateStatus(selected.id, s)}
                onAddNote={(n) => addNote(selected.id, n)}
                onClose={() => setSelectedId(null)}
              />
            ) : (
              <div className="bw-card p-8 text-center sticky top-24">
                <MessageSquare className="w-8 h-8 mx-auto mb-3" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
                <div className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>
                  Select a request to view details, update status, and add notes.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QueueCard({ item, selected, onClick }) {
  const meta = STATUS_META[item.status] || {};
  const created = new Date(item.created_at);
  const hoursAgo = Math.max(0, Math.floor((Date.now() - created.getTime()) / 3.6e6));
  return (
    <button
      onClick={onClick}
      className={`bw-card p-4 w-full text-left transition-all ${selected ? "border-2" : ""}`}
      style={{ borderColor: selected ? "var(--bw-text-primary)" : "var(--bw-border)" }}
      data-testid={`queue-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-lg truncate" style={{ color: "var(--bw-text-primary)" }}>
            {item.project?.project_type || "Unknown"} · {item.user?.name || item.user?.email || "—"}
          </div>
          <div className="text-sm mt-1 truncate" style={{ color: "var(--bw-text-secondary)" }}>
            {item.project?.address || "—"}
          </div>
        </div>
        <span
          className="flex-shrink-0 text-[10px] uppercase font-bold tracking-widest px-2 py-1 border"
          style={{ color: meta.color, borderColor: meta.color }}
        >
          {meta.label || item.status}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs mt-2" style={{ color: "var(--bw-text-tertiary)" }}>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {hoursAgo}h ago</span>
        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${item.amount_paid?.toFixed(2) || "—"}</span>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.project?.jurisdiction || "—"}</span>
      </div>
    </button>
  );
}

function QueueDetail({ item, statuses, onStatusChange, onAddNote, onClose }) {
  const [note, setNote] = useState("");
  const meta = STATUS_META[item.status] || {};

  return (
    <div className="bw-card p-6 sticky top-24" data-testid="queue-detail">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-overline mb-1" style={{ color: meta.color }}>{meta.label || item.status}</div>
          <div className="font-display font-black text-2xl" style={{ color: "var(--bw-text-primary)" }}>
            {item.project?.project_type}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-tan" data-testid="queue-detail-close">
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Customer info */}
      <div className="mb-5">
        <div className="label-overline mb-2">Customer</div>
        <div className="text-sm space-y-1">
          <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" strokeWidth={1.5} /> {item.user?.name}</div>
          <div className="flex items-center gap-2 truncate">
            <span className="w-3.5 h-3.5 inline-block" />
            <a href={`mailto:${item.user?.email}`} className="underline">{item.user?.email}</a>
          </div>
        </div>
      </div>

      {/* Project info */}
      <div className="mb-5">
        <div className="label-overline mb-2">Project</div>
        <div className="text-sm space-y-1">
          <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} /> {item.project?.address}</div>
          <div className="text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
            Jurisdiction: {item.project?.jurisdiction || "—"}
          </div>
        </div>
      </div>

      {/* Summary from interview */}
      {item.project?.summary && (
        <div className="mb-5">
          <div className="label-overline mb-2">AI Intake Summary</div>
          <div className="text-xs space-y-1" style={{ color: "var(--bw-text-secondary)" }}>
            <div>Feasibility: <strong style={{ color: "var(--bw-text-primary)" }}>{item.project.summary.feasibility}</strong></div>
            <div>Complexity: <strong style={{ color: "var(--bw-text-primary)" }}>{item.project.summary.permit_complexity}</strong></div>
            <div>Timeline: <strong style={{ color: "var(--bw-text-primary)" }}>{item.project.summary.estimated_timeline_weeks}</strong></div>
            <div>Cost range: <strong style={{ color: "var(--bw-text-primary)" }}>{item.project.summary.estimated_cost_range}</strong></div>
          </div>
        </div>
      )}

      {/* Status update */}
      <div className="mb-5">
        <div className="label-overline mb-2">Update Status</div>
        <select
          value={item.status}
          onChange={(e) => onStatusChange(e.target.value)}
          className="bw-input"
          data-testid="status-select"
        >
          {statuses.map((s) => (
            <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div>
        <div className="label-overline mb-2">Internal Notes ({item.notes?.length || 0})</div>
        <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
          {(item.notes || []).map((n) => (
            <div key={n.id} className="text-xs p-2 border" style={{ borderColor: "var(--bw-border)", backgroundColor: "var(--bw-bg-tertiary)" }}>
              <div className="font-bold" style={{ color: "var(--bw-text-primary)" }}>{n.author}</div>
              <div style={{ color: "var(--bw-text-secondary)" }}>{n.note}</div>
              <div className="text-[10px] mt-1" style={{ color: "var(--bw-text-tertiary)" }}>
                {new Date(n.ts).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Add a note (visible only to admins)…"
          className="bw-input"
          data-testid="note-input"
        />
        <button
          onClick={() => { onAddNote(note); setNote(""); }}
          disabled={!note.trim()}
          className="btn-primary w-full mt-2"
          data-testid="note-submit"
        >
          <Save className="w-4 h-4" /> Save Note
        </button>
      </div>
    </div>
  );
}
